import { useCallback, useEffect, useRef, useState } from 'react';

import { GetPageApi } from 'js/social/api/feed_api';
import { currentUser } from 'js/utils/user';
import {
  fsEvent,
  initializeAndStopFullstory,
  initializeFullstory,
} from 'js/fullstory';

export type FeedPage =
  | 'home'
  | 'profile'
  | 'single_post'
  | 'hashtag'
  | 'upload'
  | 'research_interest'
  | 'liswp';

export type FeedConfigInNewsFeedResponse = Pick<
  Awaited<ReturnType<GetPageApi>>,
  | 'openalexRecsTestBucket'
  | 'extendedOnsiteRecsTestBucket'
  | 'recencyWeightedTestBucket'
  | 'guessTheResearcherTestBucket'
  | 'topicText'
> & {
  meta?: {
    showJJHomePage?: boolean;
  };
};

/**
 * Feed configuration state management for social news feed.
 */
const useFeedConfig = (page: FeedPage) => {
  const fullStoryInitializedRef = useRef(false);

  const [openalexRecsTestBucket, setOpenalexRecsTestBucket] = useState('');
  const [extendedOnsiteRecsTestBucket, setExtendedOnsiteRecsTestBucket] =
    useState('');
  const [recencyWeightedTestBucket, setRecencyWeightedTestBucket] =
    useState('');
  const [guessTheResearcherTestBucket, setGuessTheResearcherTestBucket] =
    useState('');
  const [showJJHomePage, setShowJJHomePage] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const recommendationRatingsEnabled =
    page === 'home' && params.get('ratings') === 'true';

  useEffect(() => {
    // Only initialize FullStory once
    const shouldRecordFS =
      !!openalexRecsTestBucket || recommendationRatingsEnabled;
    if (!fullStoryInitializedRef.current && shouldRecordFS) {
      fullStoryInitializedRef.current = true;
      if (window.$rails_env === 'development') {
        return;
      }

      initializeAndStopFullstory();
      initializeFullstory(currentUser ?? undefined, true);
    }

    if (fullStoryInitializedRef.current && !!openalexRecsTestBucket) {
      fsEvent('openalexNewsFeedRecsTestV4', {
        bucket: openalexRecsTestBucket,
      });
    }

    if (fullStoryInitializedRef.current && recommendationRatingsEnabled) {
      fsEvent('newsFeedRecommendationRatings');
    }
  }, [openalexRecsTestBucket, recommendationRatingsEnabled]);

  const setFeedConfig = useCallback(
    ({
      openalexRecsTestBucket,
      extendedOnsiteRecsTestBucket,
      recencyWeightedTestBucket,
      guessTheResearcherTestBucket,
      topicText: _topicText,
      meta,
    }: FeedConfigInNewsFeedResponse) => {
      setOpenalexRecsTestBucket(openalexRecsTestBucket || '');
      setExtendedOnsiteRecsTestBucket(extendedOnsiteRecsTestBucket || '');
      setRecencyWeightedTestBucket(recencyWeightedTestBucket || '');
      setGuessTheResearcherTestBucket(guessTheResearcherTestBucket || '');
      setShowJJHomePage(meta?.showJJHomePage || false);
    },
    [],
  );

  return {
    page,
    openalexRecsTestBucket,
    extendedOnsiteRecsTestBucket,
    recencyWeightedTestBucket,
    guessTheResearcherTestBucket,
    showJJHomePage,
    recommendationRatingsEnabled,
    setFeedConfig,
  };
};

export default useFeedConfig;
